import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import ReportTable from "../../common/ReportTable/ReportTable";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter from "../../common/DateRangeFilter";
import utils from "../../../utils/utils";
import { fetchExternalEntityAccessLog } from "./ExternalUserAccessScoping";
import "./ExternalAccessActivityLog.scss";

const FIELD_OPTIONS = [
  { key: "Action", label: "Action" },
  { key: "When", label: "Perform at" },
  { key: "PerformedBy", label: "Perform by" },
  { key: "User", label: "User" },
  { key: "Study", label: "Study" },
  { key: "Site", label: "Site" },
  { key: "Category", label: "Category" },
];

const DEFAULT_COLUMNS = [
  "Action",
  "When",
  "PerformedBy",
  "User",
  "Study",
  "Site",
  "Category",
];

const ACTION_LABELS = {
  GRANT: "Study Access Grant",
  REMOVE: "Study Access Removed",
  UPDATE: "Study Access Update",
  EXPIRE: "Study Access Expired",
};

const ACTION_FILTER_OPTIONS = [
  { value: "GRANT", label: ACTION_LABELS.GRANT },
  { value: "REMOVE", label: ACTION_LABELS.REMOVE },
  { value: "UPDATE", label: ACTION_LABELS.UPDATE },
  { value: "EXPIRE", label: ACTION_LABELS.EXPIRE },
];

const EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

const COLUMN_DATE_PRESETS = [
  "Last 30 Days",
  "Last 90 Days",
  "This Year",
  "All Time",
  "Custom",
];

const FILTERS_INITIAL = {
  actions: [],
  performedAtRange: { ...EMPTY_DATE_RANGE },
  performedBy: [],
  users: [],
  studies: [],
  sites: [],
  categories: [],
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const matchesDateRange = (value, range) => {
  if (
    !range ||
    range.preset === "All Time" ||
    (!range.startDate && !range.endDate)
  ) {
    return true;
  }
  const raw = String(value ?? "").trim();
  if (!raw) return false;
  const date = utils.parseDateInput(raw);
  if (!date) return false;
  const start = range.startDate
    ? startOfDay(utils.parseDateInput(range.startDate))
    : null;
  const end = range.endDate
    ? endOfDay(utils.parseDateInput(range.endDate))
    : null;
  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
};

const formatWhen = (value) => {
  if (!value) return "—";
  const formatted = utils.formatDateTimeDisplay?.(value);
  if (formatted) return formatted;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
};

const studyLabel = (row) => {
  const name = String(row?.StudyName || "").trim();
  const number = String(row?.StudyNumber || "").trim();
  if (name && number) return `${name} (${number})`;
  return name || number || "—";
};

const uniqueSortedOptions = (values) =>
  [...new Set(values.filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((value) => ({ value, label: value }));

const mapLogRows = (entries) =>
  (Array.isArray(entries) ? entries : []).map((row, index) => {
    const action = String(row?.ActionType || "")
      .trim()
      .toUpperCase();
    return {
      id: String(row?.LogId || `${row?.PerformedOn || ""}-${index}`),
      When: formatWhen(row?.PerformedOn),
      WhenRaw: row?.PerformedOn || "",
      Action: ACTION_LABELS[action] || action || "—",
      ActionRaw: action,
      User: String(row?.StaffName || "").trim() || "—",
      Study: studyLabel(row),
      Site: String(row?.SiteName || "").trim() || "—",
      Category: String(row?.Category || "").trim() || "—",
      Details: String(row?.Details || "").trim() || "",
      PerformedBy: String(row?.PerformedByName || "").trim() || "—",
    };
  });

const applyLogFilters = (rows, filters) =>
  (rows || []).filter((row) => {
    if (
      filters.actions?.length &&
      !filters.actions.includes(row.ActionRaw)
    ) {
      return false;
    }
    if (
      filters.performedBy?.length &&
      !filters.performedBy.includes(String(row.PerformedBy || "").trim())
    ) {
      return false;
    }
    if (
      filters.users?.length &&
      !filters.users.includes(String(row.User || "").trim())
    ) {
      return false;
    }
    if (
      filters.studies?.length &&
      !filters.studies.includes(String(row.Study || "").trim())
    ) {
      return false;
    }
    if (
      filters.sites?.length &&
      !filters.sites.includes(String(row.Site || "").trim())
    ) {
      return false;
    }
    if (
      filters.categories?.length &&
      !filters.categories.includes(String(row.Category || "").trim())
    ) {
      return false;
    }
    return matchesDateRange(row.WhenRaw || row.When, filters.performedAtRange);
  });

function ActionDetailsHint({ details }) {
  const text = String(details || "").trim();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popoverRef = useRef(null);
  const closeTimerRef = useRef(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const placePopover = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = 280;
    const margin = 8;
    let left = rect.left;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - width - margin);
    }
    setCoords({ top: rect.bottom + 6, left });
  }, []);

  const openPopover = useCallback(() => {
    clearCloseTimer();
    placePopover();
    setOpen(true);
  }, [clearCloseTimer, placePopover]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  }, [clearCloseTimer]);

  const close = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (btnRef.current?.contains(event.target)) return;
      if (popoverRef.current?.contains(event.target)) return;
      close();
    };
    const onKey = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, close]);

  if (!text) return null;

  return (
    <span className="external-access-log__hint">
      <button
        ref={btnRef}
        type="button"
        className={`external-access-log__hint-btn${open ? " is-open" : ""}`}
        aria-label="View details"
        title={text}
        onClick={(event) => {
          event.stopPropagation();
          if (open) close();
          else openPopover();
        }}
        onMouseEnter={openPopover}
        onMouseLeave={scheduleClose}
      >
        <i className="fas fa-info-circle" aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              className="external-access-log__hint-popover"
              role="tooltip"
              style={{ top: coords.top, left: coords.left }}
              onMouseEnter={clearCloseTimer}
              onMouseLeave={scheduleClose}
            >
              <div className="external-access-log__hint-popover-title">
                Details
              </div>
              <div className="external-access-log__hint-popover-body">{text}</div>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

/**
 * Shared-access activity log for the selected Sponsor/CRO user.
 */
export default function ExternalAccessActivityLog({
  organizationId,
  staffId = "",
  refreshNonce = 0,
  active = true,
}) {
  const [sourceRows, setSourceRows] = useState([]);
  const [loading, setLoading] = useState(() =>
    Boolean(active && organizationId && staffId),
  );
  const [tableFilters, setTableFilters] = useState(FILTERS_INITIAL);

  const load = useCallback(async () => {
    if (!organizationId || !staffId) {
      setSourceRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    // Clear so ReportTable can show skeleton (it only skeletons when rows are empty).
    setSourceRows([]);
    try {
      const entries = await fetchExternalEntityAccessLog({
        organizationId,
        staffId,
      });
      setSourceRows(mapLogRows(entries));
    } catch (error) {
      console.error("load external access activity log", error);
      setSourceRows([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, staffId]);

  useEffect(() => {
    if (!active) return;
    load();
  }, [active, load, refreshNonce]);

  useEffect(() => {
    setTableFilters(FILTERS_INITIAL);
  }, [staffId]);

  const updateTableFilters = useCallback((patch) => {
    setTableFilters((prev) => ({ ...prev, ...patch }));
  }, []);

  const performedByOptions = useMemo(
    () =>
      uniqueSortedOptions(
        sourceRows
          .map((row) => String(row.PerformedBy || "").trim())
          .filter((name) => name && name !== "—"),
      ),
    [sourceRows],
  );

  const userOptions = useMemo(
    () =>
      uniqueSortedOptions(
        sourceRows
          .map((row) => String(row.User || "").trim())
          .filter((name) => name && name !== "—"),
      ),
    [sourceRows],
  );

  const studyOptions = useMemo(
    () =>
      uniqueSortedOptions(
        sourceRows
          .map((row) => String(row.Study || "").trim())
          .filter((name) => name && name !== "—"),
      ),
    [sourceRows],
  );

  const siteOptions = useMemo(
    () =>
      uniqueSortedOptions(
        sourceRows
          .map((row) => String(row.Site || "").trim())
          .filter((name) => name && name !== "—"),
      ),
    [sourceRows],
  );

  const categoryOptions = useMemo(() => {
    const options = uniqueSortedOptions(
      sourceRows
        .map((row) => String(row.Category || "").trim())
        .filter((name) => name && name !== "—"),
    );
    if (options.length) return options;
    return [{ value: "eISF", label: "eISF" }];
  }, [sourceRows]);

  const tableRows = useMemo(
    () => applyLogFilters(sourceRows, tableFilters),
    [sourceRows, tableFilters],
  );

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    if (
      field === "Action" ||
      field === "When" ||
      field === "PerformedBy" ||
      field === "User" ||
      field === "Study" ||
      field === "Site" ||
      field === "Category"
    ) {
      return true;
    }
    const term = String(searchValue || "").trim().toLowerCase();
    if (!term) return true;
    return String(row[field] ?? "")
      .toLowerCase()
      .includes(term);
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "Action") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={ACTION_FILTER_OPTIONS}
            value={tableFilters.actions}
            onChange={(values) => updateTableFilters({ actions: values })}
            emptyLabel="All"
            addLabel="+"
            popoverTitle="Filter by action"
            popoverSubtitle="Select one or more actions"
            showSearch
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === "When") {
        if (options?.checkOnly) return true;
        return (
          <DateRangeFilter
            value={tableFilters.performedAtRange}
            onChange={(range) =>
              updateTableFilters({
                performedAtRange: range || { ...EMPTY_DATE_RANGE },
              })
            }
            placeholder="Date range"
            presetOptions={COLUMN_DATE_PRESETS}
            compact
            disabled={options?.disabled}
          />
        );
      }

      if (field === "PerformedBy") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={performedByOptions}
            value={tableFilters.performedBy}
            onChange={(values) => updateTableFilters({ performedBy: values })}
            emptyLabel="All"
            addLabel="+"
            popoverTitle="Filter by perform by"
            popoverSubtitle="Select one or more people"
            showSearch
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === "User") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={userOptions}
            value={tableFilters.users}
            onChange={(values) => updateTableFilters({ users: values })}
            emptyLabel="All"
            addLabel="+"
            popoverTitle="Filter by user"
            popoverSubtitle="Select one or more users"
            showSearch
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === "Study") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={studyOptions}
            value={tableFilters.studies}
            onChange={(values) => updateTableFilters({ studies: values })}
            emptyLabel="All"
            addLabel="+"
            popoverTitle="Filter by study"
            popoverSubtitle="Select one or more studies"
            showSearch
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === "Site") {
        if (options?.checkOnly) return true;
        return (
          <BadgeMultiSelect
            options={siteOptions}
            value={tableFilters.sites}
            onChange={(values) => updateTableFilters({ sites: values })}
            emptyLabel="All"
            addLabel="+"
            popoverTitle="Filter by site"
            popoverSubtitle="Select one or more sites"
            showSearch
            compactSummary
            disabled={options?.disabled}
          />
        );
      }

      if (field === "Category") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search external-access-log__column-select"
            value={tableFilters.categories[0] || ""}
            disabled={options?.disabled}
            onChange={(event) =>
              updateTableFilters({
                categories: event.target.value ? [event.target.value] : [],
              })
            }
          >
            <option value="">All</option>
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      return null;
    },
    [
      categoryOptions,
      performedByOptions,
      siteOptions,
      studyOptions,
      tableFilters,
      updateTableFilters,
      userOptions,
    ],
  );

  const renderCell = useCallback((row, field) => {
    if (field === "Action") {
      const tone =
        row.ActionRaw === "GRANT"
          ? "is-grant"
          : row.ActionRaw === "REMOVE"
            ? "is-remove"
            : row.ActionRaw === "UPDATE"
              ? "is-update"
              : row.ActionRaw === "EXPIRE"
                ? "is-expire"
                : "";
      return (
        <span className="external-access-log__action-cell">
          <span className={`external-access-log__action ${tone}`}>
            {row.Action}
          </span>
          <ActionDetailsHint details={row.Details} />
        </span>
      );
    }
    return row[field] ?? "—";
  }, []);

  return (
    <div className="external-access-log">
      <div className="external-access-log__report-wrap">
        <ReportTable
          rows={tableRows}
          fieldOptions={FIELD_OPTIONS}
          defaultColumns={DEFAULT_COLUMNS}
          pageSizeOptions={[10, 25, 50]}
          columnSearchMode="client"
          persistLayoutKey={`external-access-activity-log-v4-${staffId || "user"}`}
          loading={loading}
          emptyMessage="No activity yet"
          emptyDescription="Assign, update, or remove shared study access to start this log."
          renderCell={renderCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          hideColumnHeaderActionsFields={[
            "Action",
            "When",
            "PerformedBy",
            "User",
            "Study",
            "Site",
            "Category",
          ]}
          getRowKey={(row) => row.id}
        />
      </div>
    </div>
  );
}
